import { toFixed } from "@performyard/utils-isomorphic";

import type { DecisionRow, MyDecisionsDirectReportRow } from "../../types";
import type { CompensationGroupUser, GroupBudgetFigures, IncreaseValues } from "./types";

import { toDecisionRow } from "../CellRenderers";

export const mapGroupUsersToDecisionRows = (
	groupUsers: MyDecisionsDirectReportRow[],
	userID: string | undefined
): DecisionRow[] => groupUsers.map((dr, index) => toDecisionRow(dr, index, userID));

export const buildGroupRowDataWithExpansion = (
	baseRows: DecisionRow[],
	expandedRowId: string | null,
	notesPayloadKeyFn: (row: DecisionRow) => string
): DecisionRow[] => {
	if (expandedRowId == null) {
		return baseRows;
	}
	return baseRows.flatMap((row) => {
		if (row._id !== expandedRowId) {
			return [row];
		}
		return [
			row,
			{
				...row,
				_id: `super-${row._id}`,
				superRow: true,
				parentRowId: notesPayloadKeyFn(row),
			},
		];
	});
};

export const computeGroupBudgetFigures = (
	groupUsers: CompensationGroupUser[],
	baseGroupRowData: DecisionRow[],
	increaseValues: IncreaseValues,
	recommendedIncreasePercent: number
): GroupBudgetFigures => {
	const groupCurrentSalary = groupUsers.reduce((sum, u) => sum + (u.currentSalary ?? 0), 0);
	const groupIncreaseBudget = toFixed(groupCurrentSalary * (recommendedIncreasePercent / 100), 2);
	const groupAllocated = baseGroupRowData.reduce((sum, row) => {
		const draftAmount = increaseValues[row._id]?.amount || 0;
		const amount = typeof draftAmount === "number" ? draftAmount : row.proposed_increase_amount;
		return sum + (typeof amount === "number" ? amount : 0);
	}, 0);
	const groupPercentAllocated =
		groupIncreaseBudget > 0 ? Math.round((groupAllocated / groupIncreaseBudget) * 100) : 0;
	const groupRawRemaining = groupIncreaseBudget - groupAllocated;

	const groupRemaining = Math.abs(groupRawRemaining) < 0.01 ? 0 : toFixed(groupRawRemaining, 2);
	const isGroupOverBudget = groupAllocated - groupIncreaseBudget >= 0.01;
	return {
		groupIncreaseBudget,
		groupAllocated,
		groupPercentAllocated,
		groupRemaining,
		isGroupOverBudget,
	};
};
